import { loadMemberInfo, loadRecordOfDate } from '../../../lib/api/runningtrackMonitor';

// ── 날짜 (브라우저 로컬 자정 기준 — 기존 대시보드·서버 저장 기준과 같다) ──
const WD = ['일', '월', '화', '수', '목', '금', '토'];
export const pad = (n) => (n < 10 ? '0' + n : '' + n);
export const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const toUnix = (d) => Math.floor(d.getTime() / 1000);
export const fromUnix = (sec) => new Date(Number(sec) * 1000);
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseDayKey = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
export const dayDiff = (a, b) => Math.round((dayStart(a) - dayStart(b)) / 86400000);
export const md = (d) => `${d.getMonth() + 1}/${d.getDate()}(${WD[d.getDay()]})`;
export const ymd = (d) => `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
export const clock = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
export const weekdayLabels = ['월', '화', '수', '목', '금', '토', '일'];

// ── 숫자 ──
export const num = (n) => Number(n || 0).toLocaleString('ko-KR');
export const km = (m) => `${(Number(m || 0) / 1000).toFixed(2)}km`;
export const dur = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(x)}` : `${m}:${pad(x)}`;
};
export const pace = (meters, sec) => {
  if (!meters || !sec) return '-';
  const p = Math.round(sec / (meters / 1000));
  return `${Math.floor(p / 60)}'${pad(p % 60)}"`;
};
export const maskPhone = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  return d.length >= 4 ? `010-****-${d.slice(-4)}` : '';
};

// ── 기간 (기록한 회원 방식: 빠른 선택 + 시작일~종료일 + ◀ ▶) ──
export const RANGE_PRESETS = [['today', '오늘'], ['7', '7일'], ['30', '30일'], ['month', '이번 달']];
export const presetRange = (key) => {
  const t = dayStart(new Date());
  if (key === 'today') return { key, from: t, to: t };
  if (key === 'month') return { key, from: new Date(t.getFullYear(), t.getMonth(), 1), to: t };
  const n = Number(key) || 7;
  return { key, from: addDays(t, -(n - 1)), to: t };
};
export const rangeDays = (r) => dayDiff(r.to, r.from) + 1;
export const rangeUnix = (r) => ({ startTime: toUnix(r.from), endTime: toUnix(addDays(r.to, 1)) - 1 });
export const rangeLabel = (r) =>
  rangeDays(r) === 1 ? `${md(r.from)} 하루` : `${md(r.from)} ~ ${md(r.to)} · ${rangeDays(r)}일`;
export const shiftRange = (r, dir) => {
  const len = rangeDays(r);
  const today = dayStart(new Date());
  let from = addDays(r.from, dir * len), to = addDays(r.to, dir * len);
  if (to > today) { to = today; from = addDays(today, -(len - 1)); }
  return { key: 'custom', from, to };
};
export const eachDay = (r) => {
  const out = [];
  for (let d = r.from; d <= r.to; d = addDays(d, 1)) out.push(d);
  return out;
};
// 이번 주(월요일 시작) — 서버 weeklyrank 와 같은 경계
export const thisWeekRange = () => {
  const t = dayStart(new Date());
  return { key: 'week', from: addDays(t, -((t.getDay() + 6) % 7)), to: t };
};

// ── 동시에 너무 많이 부르지 않기 ──
export const mapLimit = async (items, limit, fn) => {
  const out = new Array(items.length);
  let i = 0;
  const worker = async () => {
    while (i < items.length) {
      const idx = i++;
      try { out[idx] = await fn(items[idx], idx); } catch (e) { out[idx] = null; }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
};

// ── 회원 정보 (이름·아이디·닉네임) — 목록에서 회원마다 부르므로 캐시한다 ──
const memberCache = new Map();
export const getMemberInfo = (memberID, clubID) => {
  const key = `${clubID}_${memberID}`;
  if (!memberCache.has(key)) {
    const p = loadMemberInfo(memberID, clubID)
      .then((res) => (res && res.data && res.data.result === 1 ? res.data.info : null))
      .catch(() => { memberCache.delete(key); return null; });
    memberCache.set(key, p);
  }
  return memberCache.get(key);
};

// ── 제타트랙 기록 카드 ──
//   ★기록 숫자(거리·시간·칼로리)는 랭킹·TV 와 같은 일자 기록(GET runningtrack/record, memberrunningtrackrecord)에서 읽는다.
//     기록 & 처방(fcGroup)의 런닝 항목(fcindex 1)에는 트랩앱 트레드밀 러닝도 같은 칸에 더해져서 제타트랙만 골라낼 수 없다.
//   기록 & 처방에서는 그날 사진만 가져온다 — 러닝앱이 올린 기록 카드(/recordimage/…)만 고르고 트랩앱 사진은 뺀다.
//   기록 사진은 저장소 경로(/recordimage/record_….jpg)로 온다 — 그대로 src 에 넣으면 관리자 웹 주소로 찾아 깨진다.
//   트랩앱과 같이 오브젝트 스토리지 주소를 앞에 붙인다(업로드는 public-read). 이미 전체 주소면 그대로 둔다.
export const OBJECT_BASE_URL = 'https://kr.object.ncloudstorage.com/qnoss';
export const recordImageUrl = (path) => (/^https?:\/\//.test(path) ? path : `${OBJECT_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`);
export const isRecordCard = (u) => typeof u === 'string' && /^\/?recordimage\//.test(u.replace(OBJECT_BASE_URL, ''));
const cardCache = new Map();
export const getRecordCards = (memberID, date, socketKey) => {
  const key = `${memberID}_${date}`;
  if (!cardCache.has(key)) {
    const p = loadRecordOfDate(memberID, date, socketKey)
      .then((res) => {
        const g = res && res.data && res.data.fcGroup;
        return g && Array.isArray(g.images) ? g.images.filter(isRecordCard).map(recordImageUrl) : [];
      })
      .catch(() => { cardCache.delete(key); return null; });
    cardCache.set(key, p);
  }
  return cardCache.get(key);
};

// ── 얼굴로그인 실패 기록 합치기 (화면에서만, 서버 변경 없음) ──
//   ① 같은 기기에서 3분 안에 이어진 실패 = 한 사람의 연속 시도. 번호 로그인이 붙은 실패에서 그 묶음이 끝난다.
//      3분 = 러닝앱이 «실패 → 번호 로그인»을 같은 사람으로 보는 시간(FaceLoginReport.PendingSec 180초).
//   ② 러닝앱은 번호 로그인 회원을 마지막 실패에만 붙인다 → 같은 묶음의 앞선 실패도 그 회원으로 본다.
//   ③ 번호 로그인으로 확인된 회원이 같으면 다른 시간·기기의 묶음도 한 사람으로 합친다.
export const FAIL_GAP_SEC = 180;
// 실패 기록 API 는 최신순 최대 500건만 준다(faceFailLog.ctrl LIST_LIMIT). 이만큼 왔으면 오래된 쪽이 잘렸을 수 있다.
export const FAIL_LIST_LIMIT = 500;
export const groupFaceFails = (list) => {
  const asc = (list || []).slice().sort((a, b) => a.dateTime - b.dateTime);
  const open = {};
  const groups = [];
  asc.forEach((f) => {
    const machine = f.machineNumber || 0;
    let g = open[machine];
    if (!g || g.closed || f.dateTime - g.last > FAIL_GAP_SEC) {
      g = { machine, rows: [], closed: false, memberID: 0, memberName: '' };
      groups.push(g);
      open[machine] = g;
    }
    g.rows.push(f);
    g.last = f.dateTime;
    if (f.phoneMemberID > 0) { g.memberID = f.phoneMemberID; g.memberName = f.phoneMemberName || ''; g.closed = true; }
  });
  const persons = [];
  const byMember = {};
  groups.forEach((g, i) => {
    let p;
    if (g.memberID) {
      p = byMember[g.memberID];
      if (!p) { p = byMember[g.memberID] = { key: `m${g.memberID}`, memberID: g.memberID, memberName: g.memberName, groups: [] }; persons.push(p); }
    } else {
      p = { key: `a${i}_${g.rows[0].failKey}`, memberID: 0, memberName: '', groups: [] };
      persons.push(p);
    }
    p.groups.push(g);
    g.person = p;
  });
  persons.forEach((p) => {
    p.rows = [].concat(...p.groups.map((g) => g.rows)).sort((a, b) => b.dateTime - a.dateTime);
    p.first = p.rows[p.rows.length - 1];
    p.last = p.rows[0];
    const machines = {}, reasons = {};
    let best = null;
    p.rows.forEach((f) => {
      if (f.machineNumber) machines[f.machineNumber] = 1;
      reasons[f.reason] = (reasons[f.reason] || 0) + 1;
      if (f.bestMemberID > 0 && (!best || Number(f.bestScore) > Number(best.bestScore))) best = f;
    });
    p.machines = Object.keys(machines).map(Number).sort((a, b) => a - b);
    p.reasons = Object.entries(reasons).sort((a, b) => b[1] - a[1]);
    p.best = best;
  });
  persons.sort((a, b) => b.rows.length - a.rows.length || b.last.dateTime - a.last.dateTime);
  return { groups, persons };
};

// 얼굴로그인 실패 원인 — 러닝앱 DetectView 가 고른다(서버 faceFailLog.ctrl.js 머리말과 같은 목록).
export const FACE_FAIL_REASON = {
  back: '너무 가까움',
  center: '안내선 밖',
  closer: '너무 멂',
  frontal: '옆모습',
  unconfirmed: '1회만 일치',
  lowscore: '점수 부족',
  nomatch: '일치하는 회원 없음',
  unknown: '알 수 없음',
};
export const faceFailReasonText = (reason) => FACE_FAIL_REASON[reason] || FACE_FAIL_REASON.unknown;
